import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBookOpen,
  FaCheck,
  FaClock,
  FaLaptop,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhoneAlt,
  FaTimes,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import { Fade } from "react-reveal";
import axios from "axios";
import Header from "../../../Common/Header";
import Footer from "../../../Common/Footer";
import coursesData from "../../../Database/coursesData";
import { baseUrl } from "../../../../../store/slices/auth";
import { getLeadAttributionFields } from "../../../../../utils/leadAttribution";

type CourseDetails = {
  lead: string;
  audience: string[];
  topics: string[];
  results: string[];
  modules: string[];
  format: string;
  duration: string;
  location: string;
  teacherNote: string;
};

const defaultDetails: CourseDetails = {
  lead:
    "Практична програма Yellow Hub для тих, хто хоче швидко розібратися в темі, спробувати інструменти на реальних завданнях і отримати зрозумілий маршрут розвитку.",
  audience: ["Початківцям без досвіду", "Тим, хто хоче змінити професію", "Учасникам, яким потрібна практика"],
  topics: ["Базові інструменти напряму", "Робота з реальними кейсами", "Портфоліо та підготовка до старту"],
  results: ["Розуміння професії", "Перші практичні роботи", "План наступних кроків"],
  modules: ["Знайомство з напрямом", "Інструменти та базові навички", "Практичний проєкт", "Підготовка до роботи"],
  format: "Онлайн / офлайн",
  duration: "Короткий інтенсив",
  location: "Yellow Hub",
  teacherNote: "Викладач супроводжує групу, дає фідбек і допомагає рухатися без хаосу.",
};

const courseDetailsByPath: Record<string, Partial<CourseDetails>> = {
  "/course-details:1": {
    lead:
      "Курс про візуальне мислення, композицію та створення зрозумілих макетів для соцмереж, презентацій і простих брендових матеріалів.",
    topics: ["Композиція, колір і типографіка", "Figma, Canva, Photoshop", "Практичні макети для портфоліо"],
    results: ["Перші дизайнерські роботи", "Розуміння базових правил дизайну", "Впевненість у роботі з макетами"],
  },
  "/course-details:2": {
    lead:
      "Практичний курс для роботи в офісі: документи, комунікація, порядок у задачах і впевнена взаємодія з командою та клієнтами.",
    audience: ["Тим, хто хоче офісну роботу", "Початківцям у документообігу", "Людям, яким важлива системність"],
    topics: ["Google Docs і Microsoft Office", "Ділова комунікація", "Організація робочого дня"],
    results: ["Навички офісної роботи", "Готовність до співбесіди", "Розуміння ролі секретаря"],
  },
  "/course-details:4": {
    lead:
      "Курс для старту в клієнтській підтримці: скрипти, CRM, робота з запереченнями та спокійна комунікація у складних ситуаціях.",
    topics: ["CRM і база клієнтів", "Скрипти розмов", "Робота з запитами та конфліктами"],
    results: ["Впевнене спілкування з клієнтами", "Перші сценарії дзвінків", "Готовність до роботи в підтримці"],
  },
  "/course-details:5": {
    lead:
      "Програма для тих, хто хоче навчитися продавати етично: чути потребу клієнта, презентувати рішення та вести розмову до результату.",
    topics: ["Етапи продажу", "Заперечення і переговори", "Скрипти та практика діалогів"],
    results: ["Базова техніка продажів", "Впевненість у комунікації", "Підготовка до реальних вакансій"],
  },
  "/course-details:6": {
    lead:
      "Старт у digital-рекламі: як працює таргетинг, як запускати кампанії та читати цифри, щоб реклама не була грою навмання.",
    topics: ["Аудиторії та офери", "Google Ads, YouTube, аналітика", "Структура рекламної кампанії"],
    results: ["Перший рекламний план", "Розуміння метрик", "Портфоліо з навчальним кейсом"],
  },
  "/course-details:7": {
    lead:
      "Курс про штучний інтелект як робочий інструмент: ChatGPT, нейромережі, промпти, автоматизація й практичні сценарії для навчання та роботи.",
    topics: ["ChatGPT і промптинг", "AI для текстів, ідей і аналізу", "Безпечна робота з нейромережами"],
    results: ["Готові промпти", "AI-інструменти для щоденних задач", "Розуміння обмежень і можливостей ШІ"],
  },
  "/course-details:8": {
    lead:
      "Поглиблений курс із таргетингу для старту в професії: стратегія, креативи, запуск, оптимізація та зрозуміла аналітика.",
    topics: ["Стратегія рекламної кампанії", "Креативи та гіпотези", "Аналітика й оптимізація"],
    results: ["Кейс для портфоліо", "Навички запуску кампаній", "План входу в професію"],
  },
  "/course-details:9": {
    lead:
      "Графічний дизайн із фокусом на професійні інструменти Adobe та реальні задачі: айдентика, поліграфія, соцмережі й презентації.",
    topics: ["Adobe Photoshop, Illustrator, InDesign", "Фірмовий стиль", "Портфоліо з кількох робіт"],
    results: ["Професійні макети", "Базове дизайнерське портфоліо", "Розуміння процесу роботи з клієнтом"],
  },
  "/course-details:10": {
    lead:
      "Дитячий курс комп'ютерної грамотності: безпечна робота з комп'ютером, документи, презентації, Canva та базові цифрові навички.",
    audience: ["Дітям, які починають працювати з ПК", "Школярам для навчання", "Батькам, які хочуть системний старт"],
    topics: ["Windows і файли", "Google Docs та презентації", "Canva і базова безпека"],
    results: ["Впевнена робота за комп'ютером", "Перші навчальні проєкти", "Цифрова самостійність"],
  },
  "/course-details:11": {
    lead:
      "Професійна програма зі штучного інтелекту: від промптингу до побудови робочих процесів з AI-інструментами.",
    topics: ["ChatGPT та AI-сервіси", "Автоматизація задач", "AI-портфоліо"],
    results: ["Системне використання AI", "Практичні кейси", "План монетизації навичок"],
  },
  "/course-details:12": {
    lead:
      "Підготовка до НМТ із чіткою структурою, практикою та підтримкою, щоб учень бачив прогрес і не губився в темах.",
    audience: ["Учням, які готуються до НМТ", "Тим, хто хоче підтягнути базу", "Батькам, яким потрібна прозора система"],
    topics: ["Діагностика рівня", "План підготовки", "Практичні завдання й розбір помилок"],
    results: ["Чіткий навчальний план", "Регулярна практика", "Менше хаосу перед іспитом"],
  },
  "/course-details:13": {
    lead:
      "Школа контентмейкінгу для тих, хто хоче знімати, монтувати й упаковувати короткий відеоконтент для TikTok, Reels і Shorts.",
    topics: ["Ідеї та сценарії", "Зйомка й монтаж", "TikTok, Instagram Reels, YouTube Shorts"],
    results: ["Перші відео для портфоліо", "Контент-план", "Розуміння форматів соцмереж"],
  },
  "/course-details:14": {
    lead:
      "Дитячий розмовний клуб, де діти більше говорять англійською, слухають, повторюють живі фрази й поступово прибирають страх помилки.",
    audience: ["Дітям, яким потрібна розмовна практика", "Тим, хто знає слова, але соромиться говорити", "Мінігрупам за рівнем"],
    topics: ["Speaking practice", "Слухання та вимова", "Живі ситуації для діалогів"],
    results: ["Більше впевненості", "Активний словниковий запас", "Комфорт у мінігрупі"],
    duration: "2 заняття на тиждень",
  },
  "/course-details:15": {
    lead:
      "Групові заняття з англійської для дітей у мінігрупах 4-8 учнів: визначаємо рівень, підбираємо групу й рухаємося через практику мовлення.",
    audience: ["Дітям, які починають або продовжують англійську", "Мінігрупам 4-8 дітей", "Батькам, які хочуть видимий прогрес"],
    topics: ["Говоріння та слухання", "Слова в реальних ситуаціях", "Підбір групи за рівнем"],
    results: ["Впевненіше мовлення", "Краща вимова", "Регулярний фідбек для батьків"],
    duration: "2 заняття на тиждень",
  },
};

const fallbackCourses: Record<string, any> = {
  "/course-details:11": {
    title: "Штучний інтелект",
    thumbnail: "assets/Images/courses/15.jpg",
    authorImg: "assets/Images/advisors/advisor_vl.png",
    author: "Владислав Мінаєв",
    tag: "ChatGPT, Нейромережі",
    lessons: "12",
  },
  "/course-details:12": {
    title: "Підготовка до НМТ",
    thumbnail: "assets/Images/courses/NMTNMT.jpg",
    authorImg: "assets/Images/advisors/imgon555566666.jpg",
    author: "Команда експертів та ШІ",
    tag: "Підготовка, практика, план",
    lessons: "12",
  },
};

const mergeDetails = (coursePath: string): CourseDetails => ({
  ...defaultDetails,
  ...courseDetailsByPath[coursePath],
});

type SignupFormData = {
  firstName: string;
  lastName: string;
  phone: string;
};

type SignupModalProps = {
  open: boolean;
  courseTitle: string;
  values: SignupFormData;
  sending: boolean;
  error: string;
  success: boolean;
  onChange: (field: keyof SignupFormData, value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
};

const SignupModal = ({
  open,
  courseTitle,
  values,
  sending,
  error,
  success,
  onChange,
  onSubmit,
  onClose,
}: SignupModalProps) => {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label="Заявка на курс"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Закрити форму"
      />

      <div
        className="relative w-full max-w-[520px] overflow-hidden rounded-[28px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-3 bg-[#ffd400]" />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-5 flex h-11 w-11 items-center justify-center rounded-2xl border-2 border-black bg-white text-black shadow-[3px_3px_0_#ffd400] transition hover:-translate-y-0.5"
          aria-label="Закрити"
        >
          <FaTimes />
        </button>

        <div className="p-6 sm:p-8">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-[#c9a000]">
            Заявка на курс
          </p>
          <h2 className="mt-3 pr-12 text-[30px] font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-[38px]">
            Записатися
          </h2>
          <p className="mt-3 text-[15px] font-semibold leading-6 text-[#667085]">
            {courseTitle}
          </p>

          {success ? (
            <div className="mt-7 rounded-[22px] border-2 border-black bg-[#fff6bf] p-5 text-center shadow-[5px_5px_0_#000]">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-black text-[#ffd400]">
                <FaCheck />
              </div>
              <h3 className="mt-4 text-2xl font-black uppercase tracking-[-0.04em] text-black">
                Заявку надіслано
              </h3>
              <p className="mt-2 text-sm font-semibold leading-6 text-[#4b5563]">
                Дякуємо. Ми отримали контакт і скоро зв'яжемося з вами.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 inline-flex min-h-[48px] items-center justify-center rounded-2xl border-2 border-black bg-black px-7 text-sm font-black uppercase text-[#ffd400] shadow-[4px_4px_0_#ffd400]"
              >
                Готово
              </button>
            </div>
          ) : (
            <form className="mt-7 space-y-4" onSubmit={onSubmit}>
              <label className="block">
                <span className="mb-2 block text-sm font-black uppercase tracking-[0.08em] text-black">
                  Ім'я
                </span>
                <input
                  value={values.firstName}
                  onChange={(event) => onChange("firstName", event.target.value)}
                  className="h-14 w-full rounded-2xl border-2 border-black bg-white px-4 text-base font-bold text-black outline-none transition focus:bg-[#fff9d6] focus:shadow-[4px_4px_0_#ffd400]"
                  autoComplete="given-name"
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-black uppercase tracking-[0.08em] text-black">
                  Прізвище
                </span>
                <input
                  value={values.lastName}
                  onChange={(event) => onChange("lastName", event.target.value)}
                  className="h-14 w-full rounded-2xl border-2 border-black bg-white px-4 text-base font-bold text-black outline-none transition focus:bg-[#fff9d6] focus:shadow-[4px_4px_0_#ffd400]"
                  autoComplete="family-name"
                />
              </label>

              <label className="block">
                <span className="mb-2 flex items-center gap-2 text-sm font-black uppercase tracking-[0.08em] text-black">
                  <FaPhoneAlt className="text-[#c9a000]" />
                  Номер телефону
                </span>
                <input
                  value={values.phone}
                  onChange={(event) => onChange("phone", event.target.value)}
                  className="h-14 w-full rounded-2xl border-2 border-black bg-white px-4 text-base font-bold text-black outline-none transition focus:bg-[#fff9d6] focus:shadow-[4px_4px_0_#ffd400]"
                  placeholder="+380..."
                  autoComplete="tel"
                  inputMode="tel"
                />
              </label>

              {error && (
                <p className="rounded-2xl border-2 border-[#d92d20] bg-[#fff1f0] px-4 py-3 text-sm font-bold text-[#b42318]">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-7 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {sending ? "Надсилаємо..." : "Надіслати заявку"}
                <FaPaperPlane />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const ModernPublicCourseDetailsComponent = ({ coursePath }: { coursePath: string }) => {
  const course = coursesData.find((item: any) => item.path === coursePath) || fallbackCourses[coursePath];
  const details = mergeDetails(coursePath);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupSending, setSignupSending] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupValues, setSignupValues] = useState<SignupFormData>({
    firstName: "",
    lastName: "",
    phone: "",
  });

  const openSignup = () => {
    setSignupError("");
    setSignupSuccess(false);
    setSignupOpen(true);
  };

  const closeSignup = () => {
    if (signupSending) return;
    setSignupOpen(false);
  };

  const handleSignupChange = (field: keyof SignupFormData, value: string) => {
    setSignupValues((current) => ({ ...current, [field]: value }));
  };

  const validateSignup = () => {
    const firstName = signupValues.firstName.trim();
    const lastName = signupValues.lastName.trim();
    const phone = signupValues.phone.trim();
    const phoneDigits = phone.replace(/\D/g, "");

    if (!firstName || !lastName || !phone) {
      return "Заповніть ім'я, прізвище і номер телефону.";
    }

    if (phoneDigits.length < 9) {
      return "Введіть коректний номер телефону.";
    }

    return "";
  };

  const sendTelegramSignup = async () => {
    await axios.post(`${baseUrl}telegram/lead-request`, {
      title: "✅ Нова заявка на курс",
      fields: {
        Курс: course.title,
        Викладач: course.author,
        "Ім'я": signupValues.firstName.trim(),
        Прізвище: signupValues.lastName.trim(),
        Телефон: signupValues.phone.trim(),
        ...getLeadAttributionFields(),
      },
    });
  };

  const handleSignupSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationError = validateSignup();
    if (validationError) {
      setSignupError(validationError);
      return;
    }

    setSignupError("");
    setSignupSending(true);

    try {
      await sendTelegramSignup();
      setSignupSuccess(true);
      setSignupValues({ firstName: "", lastName: "", phone: "" });
    } catch (error: any) {
      setSignupError(error?.message || "Не вдалося надіслати заявку. Спробуйте ще раз.");
    } finally {
      setSignupSending(false);
    }
  };

  if (!course) {
    return (
      <section className="bg-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-black uppercase text-black">Курс не знайдено</h1>
          <Link
            to="/courses"
            className="mt-8 inline-flex items-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-6 py-3 text-sm font-black uppercase text-black shadow-[4px_4px_0_#000]"
          >
            <FaArrowLeft /> До курсів
          </Link>
        </div>
        <Footer />
      </section>
    );
  }

  const facts = [
    { icon: <FaBookOpen />, label: "Занять", value: `${course.lessons || "8+"}` },
    { icon: <FaClock />, label: "Тривалість", value: details.duration },
    { icon: <FaLaptop />, label: "Формат", value: details.format },
    { icon: <FaMapMarkerAlt />, label: "Локація", value: details.location },
  ];

  return (
    <section className="bg-white text-black">
      <Header />
      <SignupModal
        open={signupOpen}
        courseTitle={course.title}
        values={signupValues}
        sending={signupSending}
        error={signupError}
        success={signupSuccess}
        onChange={handleSignupChange}
        onSubmit={handleSignupSubmit}
        onClose={closeSignup}
      />

      <main>
        <div className="border-b border-[#eceff3] bg-[#fafafa]">
          <div className="container mx-auto px-4 py-4">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.08em] text-[#6b7280] transition hover:text-black"
            >
              <FaArrowLeft />
              Усі курси
            </Link>
          </div>
        </div>

        <section className="relative overflow-hidden bg-white">
          <div className="pointer-events-none absolute right-0 top-0 hidden h-full w-[32%] bg-[#ffd400] lg:block" />
          <div className="pointer-events-none absolute right-12 top-16 hidden h-40 w-72 bg-[repeating-linear-gradient(135deg,rgba(0,0,0,0.18)_0_2px,transparent_2px_12px)] lg:block" />

          <div className="container relative mx-auto px-4 py-10 md:py-14 lg:py-16">
            <div className="grid grid-cols-12 gap-8 lg:items-center">
              <div className="col-span-12 lg:col-span-7">
                <Fade bottom>
                  <p className="text-sm font-black uppercase tracking-[0.22em] text-[#c9a000]">
                    Yellow Hub / Курси
                  </p>
                  <h1 className="mt-4 text-[42px] font-black uppercase leading-[0.95] tracking-[-0.055em] text-black sm:text-[58px] lg:text-[76px]">
                    {course.title}
                  </h1>
                  <p className="mt-6 max-w-[760px] text-[17px] font-semibold leading-8 text-[#4b5563] sm:text-[19px]">
                    {details.lead}
                  </p>
                </Fade>

                <Fade bottom>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={openSignup}
                      className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-7 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-0.5 hover:shadow-[7px_7px_0_#000]"
                    >
                      Записатися
                      <FaArrowRight />
                    </button>
                    <Link
                      to="/courses"
                      className="inline-flex min-h-[54px] items-center justify-center rounded-2xl border-2 border-black bg-white px-7 text-sm font-black uppercase text-black shadow-[5px_5px_0_#ffd400] transition hover:-translate-y-0.5"
                    >
                      Інші курси
                    </Link>
                  </div>
                </Fade>
              </div>

              <div className="col-span-12 lg:col-span-5">
                <Fade bottom>
                  <div className="relative rounded-[28px] border-2 border-black bg-white p-4 shadow-[8px_8px_0_#000]">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="aspect-[1.18/1] w-full rounded-[20px] bg-[#f6f7f9] object-contain p-5"
                    />
                  </div>
                </Fade>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {facts.map((fact) => (
                <Fade bottom key={fact.label}>
                  <div className="rounded-[22px] border border-[#e5e7eb] bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.06)]">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-black text-[#ffd400]">
                      {fact.icon}
                    </div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a8f98]">
                      {fact.label}
                    </p>
                    <p className="mt-1 text-[16px] font-black leading-snug text-black">
                      {fact.value}
                    </p>
                  </div>
                </Fade>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f7f8fb] py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-12 gap-5">
              {[
                { title: "Для кого", icon: <FaUsers />, items: details.audience },
                { title: "Що вивчаємо", icon: <FaLaptop />, items: details.topics },
                { title: "Результат", icon: <FaCheck />, items: details.results },
              ].map((block) => (
                <Fade bottom key={block.title}>
                  <div className="col-span-12 h-full rounded-[26px] border-2 border-black bg-white p-6 shadow-[6px_6px_0_#ffd400] md:col-span-4">
                    <div className="mb-5 flex items-center gap-3">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#ffd400] text-black">
                        {block.icon}
                      </span>
                      <h2 className="text-2xl font-black uppercase tracking-[-0.04em] text-black">
                        {block.title}
                      </h2>
                    </div>
                    <ul className="space-y-3">
                      {block.items.map((item) => (
                        <li key={item} className="flex gap-3 text-[15px] font-bold leading-6 text-[#344054]">
                          <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-black text-[10px] text-[#ffd400]">
                            <FaCheck />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Fade>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 lg:col-span-7">
                <Fade bottom>
                  <p className="text-sm font-black uppercase tracking-[0.2em] text-[#c9a000]">
                    Програма
                  </p>
                  <h2 className="mt-3 text-[38px] font-black uppercase leading-none tracking-[-0.05em] text-black sm:text-[52px]">
                    Як проходить навчання
                  </h2>
                </Fade>

                <div className="mt-8 space-y-4">
                  {details.modules.map((module, index) => (
                    <Fade bottom key={module}>
                      <div className="flex gap-4 rounded-[24px] border border-[#e5e7eb] bg-white p-5 shadow-[0_12px_34px_rgba(15,23,42,0.06)]">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-black bg-[#ffd400] text-lg font-black shadow-[3px_3px_0_#000]">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a8f98]">
                            Крок {index + 1}
                          </p>
                          <h3 className="mt-1 text-xl font-black leading-tight text-black">
                            {module}
                          </h3>
                        </div>
                      </div>
                    </Fade>
                  ))}
                </div>
              </div>

              <div className="col-span-12 lg:col-span-5">
                <Fade bottom>
                  <aside className="sticky top-28 rounded-[28px] border-2 border-black bg-black p-6 text-white shadow-[8px_8px_0_#ffd400]">
                    <p className="text-sm font-black uppercase tracking-[0.18em] text-[#ffd400]">
                      Викладач
                    </p>
                    <div className="mt-5 flex items-center gap-4">
                      <img
                        src={course.authorImg}
                        alt={course.author}
                        className="h-20 w-20 rounded-2xl border-2 border-[#ffd400] object-cover"
                      />
                      <div>
                        <h3 className="text-2xl font-black leading-tight text-white">
                          {course.author}
                        </h3>
                        <p className="mt-1 text-sm font-bold text-white/60">{course.tag}</p>
                      </div>
                    </div>
                    <p className="mt-6 text-[15px] font-semibold leading-7 text-white/78">
                      {details.teacherNote}
                    </p>
                    <div className="mt-6 rounded-2xl border border-white/15 bg-white/10 p-4">
                      <div className="flex items-center gap-3">
                        <FaUserTie className="text-[#ffd400]" />
                        <span className="text-sm font-black uppercase tracking-[0.08em] text-white">
                          Практика + фідбек
                        </span>
                      </div>
                    </div>
                  </aside>
                </Fade>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#ffd400] py-12 md:py-16">
          <div className="container mx-auto px-4">
            <Fade bottom>
              <div className="flex flex-col gap-6 rounded-[28px] border-2 border-black bg-white p-7 shadow-[8px_8px_0_#000] lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-[#c9a000]">
                    Старт
                  </p>
                  <h2 className="mt-2 text-[34px] font-black uppercase leading-none tracking-[-0.04em] text-black sm:text-[46px]">
                    Хочеш на цей курс?
                  </h2>
                  <p className="mt-3 max-w-[720px] text-[16px] font-semibold leading-7 text-[#4b5563]">
                    Залиш заявку в кабінеті, і команда Yellow Hub допоможе з наступним кроком.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openSignup}
                  className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border-2 border-black bg-black px-7 text-sm font-black uppercase text-[#ffd400] shadow-[5px_5px_0_#ffd400] transition hover:-translate-y-0.5"
                >
                  Записатися
                  <FaArrowRight />
                </button>
              </div>
            </Fade>
          </div>
        </section>
      </main>

      <Footer />
    </section>
  );
};

export default ModernPublicCourseDetailsComponent;
